/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteSessionsDetachBinding } from "../funcs/remoteSessionsDetachBinding.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DetachBindingRequest,
  DetachBindingSecurity,
} from "../models/operations/detachbinding.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoteSessionsDetachBindingMutationVariables = {
  request: DetachBindingRequest;
  security?: DetachBindingSecurity | undefined;
  options?: RequestOptions;
};

export type RemoteSessionsDetachBindingMutationData = void;

export type RemoteSessionsDetachBindingMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * detachBinding remoteSessions
 *
 * @remarks
 * Manage exact remote session attachments for an agent. Requires an ordinary human session, agent authorization authority and ownership of the upstream session. Never returns credentials.
 */
export function useRemoteSessionsDetachBindingMutation(
  options?: MutationHookOptions<
    RemoteSessionsDetachBindingMutationData,
    RemoteSessionsDetachBindingMutationError,
    RemoteSessionsDetachBindingMutationVariables
  >,
): UseMutationResult<
  RemoteSessionsDetachBindingMutationData,
  RemoteSessionsDetachBindingMutationError,
  RemoteSessionsDetachBindingMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoteSessionsDetachBindingMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoteSessionsDetachBinding(): MutationKey {
  return ["@gram/client", "remoteSessions", "detachBinding"];
}

export function buildRemoteSessionsDetachBindingMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoteSessionsDetachBindingMutationVariables,
  ) => Promise<RemoteSessionsDetachBindingMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoteSessionsDetachBinding(),
    mutationFn: function remoteSessionsDetachBindingMutationFn({
      request,
      security,
      options,
    }): Promise<RemoteSessionsDetachBindingMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteSessionsDetachBinding(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
